import React from 'react';
import { ArrowRight, Check, Globe2, MapPin, Search } from 'lucide-react';
import { UK_URL, UK_UULE } from '../lib/exampleUrls';

const STEPS = [
  { icon: Search, title: 'Enter a keyword', body: 'Start with the query you want to inspect.' },
  { icon: Globe2, title: 'Set the market', body: 'Choose a country and the Google language.' },
  { icon: MapPin, title: 'Open the result', body: 'Add a city when the local view matters.' },
];

const USE_CASES = [
  'Check results in a specific country or city.',
  "Review the SERP your client's market sees.",
  'Compare markets without changing your IP address.',
];

const MINI_FAQ = [
  { q: 'Is it really free?', a: 'Yes. There is no signup, credit card, or query limit.' },
  { q: 'Do I need a VPN?', a: 'No. The location is sent to Google via URL parameters, so your own IP never changes.' },
  { q: 'Which countries are supported?', a: 'More than 230 countries and territories, with optional city-level targeting when you add a city.' },
  { q: 'Are my searches stored?', a: 'No. Searches run in your browser and open on google.com; nothing is saved on our side.' },
];

const HomeSections = () => {
  return (
    <>
      <section className="home-band home-band-white">
        <div className="container-max max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="home-section-intro">
            <p className="eyebrow">Built for everyday SEO work</p>
            <h2>Get a useful answer in one search.</h2>
            <p>Check a country-wide result or narrow it down to a specific city. Your query opens directly on Google with the location signal applied.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="workflow-step">
                  <div className="workflow-step-number">0{i + 1}</div>
                  <div className="workflow-icon">
                    <Icon aria-hidden="true" className="w-5 h-5" />
                  </div>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-8 text-center text-sm">
            <a href="/how-to-use/" className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-semibold">
              See how location targeting works <ArrowRight aria-hidden="true" className="w-4 h-4" />
            </a>
          </p>
        </div>
      </section>

      <section className="home-band home-band-white border-t border-slate-200" aria-labelledby="how-url-built">
        <div className="container-max max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="eyebrow">Under the hood</p>
          <h2 id="how-url-built" className="text-2xl sm:text-3xl font-semibold text-slate-900">How this tool builds the Google URL</h2>
          <p className="mt-4 text-slate-600 leading-7">
            Every search becomes an ordinary google.com link. For "running shoes" in London, United Kingdom, in English, the tool opens:
          </p>
          <pre className="mt-4 overflow-x-auto rounded border border-slate-200 bg-slate-50 p-4 text-xs sm:text-sm leading-6 text-slate-800 whitespace-pre-wrap break-all"><code>{UK_URL}</code></pre>
          <ul className="mt-4 space-y-2 text-slate-600 leading-7">
            <li><span className="font-mono text-slate-800">gl=gb</span> is the country code and <span className="font-mono text-slate-800">hl=en</span> the interface language. On its own, <span className="font-mono">gl=gb</span> already gave us UK shops priced in pounds.</li>
            <li>
              <span className="font-mono text-slate-800 break-all">uule={UK_UULE}</span> carries the place. The tool looks London up in OpenStreetMap and writes its latitude and longitude into it, base64-encoded after the <span className="font-mono">a</span> prefix; <span className="font-mono">ll</span> repeats the same coordinates.
            </li>
            <li>There is also a uule format that carries a place name instead of coordinates. Google ignored it in every test we ran, so the tool writes cities and countries as coordinates (a country uses its capital).</li>
            <li><span className="font-mono text-slate-800">pws=0</span> asks Google not to personalize the page. Nothing else is added, and your keyword is not stored on our servers.</li>
          </ul>
          <p className="mt-6 text-sm">
            <a href="/google-location-changer/" className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-semibold">
              Read the full guide: uule, gl and hl explained with real examples <ArrowRight aria-hidden="true" className="w-4 h-4" />
            </a>
          </p>
        </div>
      </section>

      <section className="home-band home-band-mint">
        <div className="container-max max-w-5xl px-4 sm:px-6 lg:px-8 home-split">
          <div>
            <p className="eyebrow">Local results, without a VPN</p>
            <h2>Google does not show everyone the same search result.</h2>
            <p className="mt-4 text-slate-600 leading-7">Country, language, and local intent can change a SERP. This tool passes the chosen location straight to Google, without changing your connection.</p>
            <p className="mt-4 text-sm">
              <a href="/features/" className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-semibold">
                Explore the SERP checker features <ArrowRight aria-hidden="true" className="w-4 h-4" />
              </a>
            </p>
          </div>
          <ul className="practical-list">
            {USE_CASES.map((useCase) => (
              <li key={useCase}>
                <Check aria-hidden="true" className="w-5 h-5 text-brand-600 flex-none" />
                {useCase}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="home-band home-band-white">
        <div className="container-max max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="eyebrow">Common questions</p>
            <h2 className="home-section-heading">Straight answers.</h2>
          </div>
          <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
            {MINI_FAQ.map((faq) => (
              <details key={faq.q} className="faq-row">
                <summary>{faq.q}</summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
          <p className="mt-6 text-center text-sm">
            <a href="/faq/" className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-semibold">
              Read the full FAQ <ArrowRight aria-hidden="true" className="w-4 h-4" />
            </a>
          </p>
        </div>
      </section>

      <section className="home-band home-band-dark">
        <div className="container-max max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-brand-300 font-semibold text-sm">Ready when you are</p>
          <h2>See the SERP that matters to you.</h2>
          <a href="#serp-checker" className="home-cta inline-flex items-center justify-center min-h-[48px] px-6 mt-6">
            Check a SERP <ArrowRight aria-hidden="true" className="w-4 h-4 ml-2" />
          </a>
        </div>
      </section>
    </>
  );
};

export default HomeSections;
